<script lang="ts">
import { Tooltip as PrimeTooltip } from 'primevue'
import { defineComponent, h, mergeProps, withDirectives } from 'vue'
import type { Directive, PropType } from 'vue'

type Placement = 'top' | 'bottom' | 'left' | 'right'

/**
 * Focus shows the tooltip only when it is keyboard focus (`:focus-visible`). Focus a script moves
 * after a mouse click (e.g. onto the «Развернуть…» button that replaced the clicked one) does not:
 * the mouse user gets the tooltip by hovering.
 */
function keyboardFocus(target: EventTarget | null) {
    try {
        return !(target instanceof Element) || target.matches(':focus-visible')
    } catch {
        // Engines without :focus-visible (test DOMs) keep showing it on any focus.
        return true
    }
}
function focusIn(event: FocusEvent) {
    if (!keyboardFocus(event.target)) return
    event.currentTarget?.dispatchEvent(new MouseEvent('mouseenter'))
}
function focusOut(event: FocusEvent) {
    event.currentTarget?.dispatchEvent(new MouseEvent('mouseleave'))
}

export default defineComponent({
    name: 'Tooltip',
    inheritAttrs: false,
    props: {
        content: { type: String, required: true },
        placement: { type: String as PropType<Placement>, default: 'top' },
        focusable: { type: Boolean, default: true },
    },
    setup(props, { attrs, slots }) {
        return () =>
            withDirectives(
                h(
                    'span',
                    mergeProps(attrs, {
                        class: 'hb-tooltip-anchor',
                        tabindex: props.focusable ? 0 : undefined,
                        onFocusin: focusIn,
                        onFocusout: focusOut,
                    }),
                    slots.default?.(),
                ),
                [
                    [
                        PrimeTooltip as Directive,
                        { value: props.content },
                        undefined,
                        { [props.placement]: true },
                    ],
                ],
            )
    },
})
</script>
